import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { ArrowRight, ArrowLeft, ChevronDown, Check, X, Minimize2, Maximize2, ThumbsUp, ThumbsDown, Move, Copy, Expand } from 'lucide-react';
import { aiApi } from '../../services/api';
import type { ChatMode } from '../../services/api';
import StudyMaterialRenderer from '../StudyMaterialRenderer';

interface ChatWidgetProps {
  sessionId: number;
  isOpen: boolean;
  onClose: () => void;
  chatHistory: Array<{type: 'user' | 'ai', message: string}>;
  onChatUpdate: (message: {type: 'user' | 'ai', message: string}) => void;
  isRTL: boolean;
  messageFeedback?: {[messageIndex: number]: 'like' | 'dislike' | null};
  onMessageFeedback?: (messageIndex: number, feedback: 'like' | 'dislike') => void;
  customChatHandler?: (message: string, mode: ChatMode) => Promise<{ response: string }>;
  pendingMessage?: string | null;
  onPendingMessageHandled?: () => void;
  onMinimizedStateChange?: (isMinimized: boolean) => void;
  unminimizeChat?: boolean;
  assistantName?: string;
  assistantAvatarSrc?: string;
  welcomeMessage?: string;
  inputPlaceholder?: string;
  overlayContent?: React.ReactNode;
  isOverlayVisible?: boolean;
  isInputDisabled?: boolean;
  mode?: ChatMode;
  onModeChange?: (mode: ChatMode) => Promise<void> | void;
  enableModeSwitch?: boolean;
  availableModes?: ChatMode[];
  showCloseButton?: boolean;
  allowResize?: boolean;
  showMinimizeButton?: boolean;
  embedded?: boolean;
}

// Memoized chat message component to prevent unnecessary re-renders
const ChatMessage = React.memo(({ 
  msg, 
  index, 
  effectiveRTL, 
  messageFeedback,
  onMessageFeedback,
  assistantName,
  assistantAvatarSrc
}: { 
  msg: {type: 'user' | 'ai', message: string}; 
  index: number; 
  effectiveRTL: boolean;
  messageFeedback?: {[messageIndex: number]: 'like' | 'dislike' | null};
  onMessageFeedback?: (messageIndex: number, feedback: 'like' | 'dislike') => void;
  assistantName: string;
  assistantAvatarSrc: string;
}) => {
  const [copySuccess, setCopySuccess] = useState(false);

  const handleCopy = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(msg.message);
      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 2000);
    } catch (err) {
      console.error('Failed to copy text:', err);
    }
  }, [msg.message]);

  return (
    <div
      className={`flex ${
        effectiveRTL
          ? (msg.type === 'user' ? 'justify-start' : 'justify-end')
          : (msg.type === 'user' ? 'justify-end' : 'justify-start')
      }`}
      style={{ direction: effectiveRTL ? 'rtl' : 'ltr' }}
    >
      <div
        className={`max-w-[80%] p-2 rounded-2xl ${
          msg.type === 'user'
            ? 'bg-gradient-to-r from-vibrant-orange to-vibrant-orange/90 text-white'
            : 'bg-surface-muted/10 text-surface-primary border border-surface-muted/30'
        }`}
      >
        {msg.type === 'ai' && (
          <div className={`flex items-center mb-2 ${effectiveRTL ? 'space-x-reverse space-x-2' : 'space-x-2'}`}>
            <div className="w-10 h-10 bg-gradient-to-br from-vibrant-orange to-vibrant-orange/90 rounded-full flex items-center justify-center flex-shrink-0">
              <img
                src={assistantAvatarSrc}
                alt={assistantName}
                className="w-20 h-20 object-contain"
              />
            </div>
            <span className={`text-md text-surface-muted font-medium ${effectiveRTL ? 'text-right' : ''}`}>
              {assistantName}
            </span>
          </div>
        )}
        {msg.type === 'ai' ? (
          <div className="text-right text-xs select-text cursor-text" dir="rtl">
            <StudyMaterialRenderer
              content={msg.message}
              materialType="chat"
              isRTL={true}
            />
          </div>
        ) : (
          <p className="text-right leading-relaxed text-xs select-text cursor-text">{msg.message}</p>
        )}
        
        {/* Copy button for user messages */}
        {msg.type === 'user' && (
          <div className={`flex items-center justify-end gap-1 px-2 pb-1 pt-2 mt-2 border-t border-white/20 ${effectiveRTL ? 'space-x-reverse' : ''}`}>
            <button
              onClick={handleCopy}
              className={`p-1 rounded hover:bg-white/20 transition-colors ${
                copySuccess 
                  ? 'bg-green-100 text-green-600' 
                  : 'text-white/80 hover:text-white'
              }`}
              title={effectiveRTL ? 'העתק' : 'Copy'}
            >
              <Copy className="w-3 h-3" />
            </button>
          </div>
        )}
        
        {/* Copy button and Like/Dislike buttons for AI messages */}
        {msg.type === 'ai' && (
          <div className={`flex items-center justify-end gap-1 px-2 pb-1 pt-2 mt-2 border-t border-surface-muted/20 ${effectiveRTL ? 'space-x-reverse' : ''}`}>
            <button
              onClick={handleCopy}
              className={`p-1 rounded hover:bg-surface-muted/20 transition-colors ${
                copySuccess 
                  ? 'bg-green-100 text-green-600' 
                  : 'text-surface-muted hover:text-surface-primary'
              }`}
              title={effectiveRTL ? 'העתק' : 'Copy'}
            >
              <Copy className="w-3 h-3" />
            </button>
            {onMessageFeedback && (
              <>
                <button
                  onClick={() => onMessageFeedback(index, 'like')}
                  className={`p-1 rounded hover:bg-surface-muted/20 transition-colors ${
                    messageFeedback?.[index] === 'like' 
                      ? 'bg-green-100 text-green-600' 
                      : 'text-surface-muted hover:text-surface-primary'
                  }`}
                  title={effectiveRTL ? 'שימושי' : 'Useful'}
                >
                  <ThumbsUp className="w-3 h-3" />
                </button>
                <button
                  onClick={() => onMessageFeedback(index, 'dislike')}
                  className={`p-1 rounded hover:bg-surface-muted/20 transition-colors ${
                    messageFeedback?.[index] === 'dislike' 
                      ? 'bg-red-100 text-red-600' 
                      : 'text-surface-muted hover:text-surface-primary'
                  }`}
                  title={effectiveRTL ? 'לא שימושי' : 'Not useful'}
                >
                  <ThumbsDown className="w-3 h-3" />
                </button>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
});

ChatMessage.displayName = 'ChatMessage';

const DEFAULT_CHAT_MODES: ChatMode[] = ['fast', 'smart'];

const ChatWidget: React.FC<ChatWidgetProps> = ({
  sessionId,
  isOpen,
  onClose,
  chatHistory,
  onChatUpdate,
  isRTL,
  messageFeedback = {},
  onMessageFeedback,
  customChatHandler,
  pendingMessage,
  onPendingMessageHandled,
  onMinimizedStateChange,
  unminimizeChat,
  assistantName,
  assistantAvatarSrc,
  welcomeMessage,
  inputPlaceholder,
  overlayContent,
  isOverlayVisible,
  isInputDisabled,
  mode,
  onModeChange,
  enableModeSwitch = true,
  availableModes = DEFAULT_CHAT_MODES,
  showCloseButton = true,
  allowResize = true,
  showMinimizeButton = true,
  embedded = false
}) => {
  const { t, i18n } = useTranslation();
  const effectiveRTL = isRTL ?? i18n.language === 'he';

  const availableModeOptions = useMemo(() => {
    const unique = Array.from(new Set(availableModes)) as ChatMode[];
    const filtered = unique.filter((modeOption): modeOption is ChatMode => modeOption === 'fast' || modeOption === 'smart');
    return filtered.length ? filtered : DEFAULT_CHAT_MODES;
  }, [availableModes]);

  const isControlledMode = mode !== undefined;
  const [internalMode, setInternalMode] = useState<ChatMode>(mode ?? 'fast');
  useEffect(() => {
    if (mode) {
      setInternalMode(mode);
    }
  }, [mode]);

  const resolvedMode = isControlledMode ? (mode as ChatMode) : internalMode;
  const [isSwitchingMode, setIsSwitchingMode] = useState(false);
  const [modeSwitchError, setModeSwitchError] = useState<string | null>(null);
  const [isModeDropdownOpen, setIsModeDropdownOpen] = useState(false);
  const modeDropdownRef = useRef<HTMLDivElement | null>(null);

  const hasCurrentHistory = chatHistory.length > 0;

  useEffect(() => {
    if (!hasCurrentHistory) {
      setModeSwitchError(null);
    }
  }, [hasCurrentHistory]);

  useEffect(() => {
    if (isSwitchingMode && isModeDropdownOpen) {
      setIsModeDropdownOpen(false);
    }
  }, [isSwitchingMode, isModeDropdownOpen]);

  useEffect(() => {
    if (!isModeDropdownOpen) {
      return;
    }

    const handleClickOutside = (event: MouseEvent) => {
      if (modeDropdownRef.current && !modeDropdownRef.current.contains(event.target as Node)) {
        setIsModeDropdownOpen(false);
      }
    };

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsModeDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscape);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isModeDropdownOpen]);

  const modePickerLabel = t('sessions:flow.chat.modePickerLabel', effectiveRTL ? 'מצב תגובה' : 'Response mode');
  const modeSwitchErrorMessage = t('sessions:flow.chat.modeSwitchError', effectiveRTL ? 'לא הצלחתי להחליף מצב. נסו שוב.' : 'Couldn\'t switch modes. Please try again.');
  const fastLabel = t('sessions:flow.chat.modes.fast', 'Fast');
  const smartLabel = t('sessions:flow.chat.modes.smart', 'Smart');
  const modeLabels: Record<ChatMode, string> = useMemo(() => ({
    fast: fastLabel,
    smart: smartLabel
  }), [fastLabel, smartLabel]);

  const resolvedAssistantName = assistantName ?? t('sessions:flow.chat.title', 'StudyBel Astronaut');
  const resolvedAssistantAvatarSrc = assistantAvatarSrc ?? '/logo_v2.png';
  const resolvedWelcomeMessage = welcomeMessage ?? t('sessions:flow.chat.welcome', 'Hello! I\'m your AI study assistant. How can I help you today?');
  const resolvedInputPlaceholder = inputPlaceholder ?? t('sessions:flow.chat.placeholder', effectiveRTL ? 'תשאלו את בוב' : 'Ask Bob');
  const [message, setMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const handleModeSelect = useCallback(async (newMode: ChatMode) => {
    if (newMode === resolvedMode) {
      setIsModeDropdownOpen(false);
      return;
    }
    if (isSwitchingMode) return;
    if (isLoading) return;
    if (!availableModeOptions.includes(newMode)) return;

    setModeSwitchError(null);
    setIsSwitchingMode(true);
    try {
      if (onModeChange) {
        await onModeChange(newMode);
      }
      if (!isControlledMode) {
        setInternalMode(newMode);
      }
    } catch (error) {
      console.error('Failed to switch chat mode', error);
      setModeSwitchError(modeSwitchErrorMessage);
    } finally {
      setIsSwitchingMode(false);
      setIsModeDropdownOpen(false);
    }
  }, [resolvedMode, isSwitchingMode, isLoading, availableModeOptions, onModeChange, isControlledMode, modeSwitchErrorMessage]);

  const [isMinimized, setIsMinimized] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [preFullscreenSize, setPreFullscreenSize] = useState<{width: number, height: number} | null>(null);
  const [preFullscreenPosition, setPreFullscreenPosition] = useState<{x: number, y: number} | null>(null);
  const [savedScrollPosition, setSavedScrollPosition] = useState<number | null>(null);

  const ArrowIcon = effectiveRTL ? ArrowLeft : ArrowRight;
  const isSendDisabled = !message.trim() || isLoading || !!isInputDisabled || isSwitchingMode;
  const modePickerDisabled = isLoading || isSwitchingMode;
  
  // Mobile detection
  const isMobile = useMemo(() => {
    return typeof window !== 'undefined' && window.innerWidth < 1024; // lg breakpoint
  }, []);
  
  const [position, setPosition] = useState(() => {
    if (embedded || typeof window === 'undefined') {
      return { x: 0, y: 0 };
    }
    
    if (window.innerWidth < 1024) { // Mobile
      return { x: 0, y: 64 }; // Full screen starting from navbar (64px)
    }
    
    return {
      x: Math.max(16, window.innerWidth - 384 - 16),
      y: 80 // Position below navbar (navbar is typically 64px + some margin)
    };
  });
  
  const [size, setSize] = useState(() => {
    if (embedded || typeof window === 'undefined') {
      return { width: 0, height: 0 };
    }
    
    if (window.innerWidth < 1024) { // Mobile
      return { 
        width: window.innerWidth, 
        height: window.innerHeight - 64 // Full screen minus navbar
      };
    }
    
    return { 
      width: 384, 
      height: window.innerHeight - 120 // Almost screen height minus space for navbar and margins
    };
  });
  const [isDragging, setIsDragging] = useState(false);
  const [isResizing, setIsResizing] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [resizeStart, setResizeStart] = useState({ x: 0, y: 0, width: 0, height: 0 });
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const savedScrollY = useRef<number | null>(null);

  const scrollToBottom = useCallback(() => {
    if (messagesContainerRef.current) {
      messagesContainerRef.current.scrollTop = messagesContainerRef.current.scrollHeight;
    }
  }, []);

  // Function to handle unminimizing with proper positioning
  const handleUnminimize = useCallback(() => {
    if (effectiveRTL && !isMobile) {
      // For RTL, restore to original position (expand from top-right)
      setPosition(prev => ({ 
        ...prev, 
        x: Math.max(0, prev.x - (size.width - 120))
      }));
    }
    setIsMinimized(false);
    setTimeout(scrollToBottom, 100);
  }, [effectiveRTL, isMobile, size.width, scrollToBottom]);
  
  // Handle unminimize request
  useEffect(() => {
    if (unminimizeChat && isMinimized) {
      handleUnminimize();
    }
  }, [unminimizeChat, isMinimized, handleUnminimize]);
  
  // Notify parent when minimized state changes
  useEffect(() => {
    if (onMinimizedStateChange) {
      onMinimizedStateChange(isMinimized);
    }
  }, [isMinimized, onMinimizedStateChange]);

  useEffect(() => {
    if (isOpen && !isMinimized) {
      // Use setTimeout to ensure DOM is updated before scrolling
      setTimeout(scrollToBottom, 100);
    }
  }, [chatHistory, isOpen, isMinimized, isFullscreen, scrollToBottom]);

  useEffect(() => {
    if (!embedded && isMobile && isOpen && !isMinimized) {
      const currentScroll = window.pageYOffset;
      savedScrollY.current = currentScroll;
      
      // Lock body scroll
      document.body.style.position = 'fixed';
      document.body.style.top = `-${currentScroll}px`;
      document.body.style.width = '100%';
      document.body.style.overflow = 'hidden';
      
      return () => {
        // Unlock body scroll and restore position
        document.body.style.position = '';
        document.body.style.top = '';
        document.body.style.width = '';
        document.body.style.overflow = '';
        
        if (savedScrollY.current !== null) {
          window.scrollTo(0, savedScrollY.current);
        }
      };
    }
  }, [isOpen, isMinimized, isMobile, embedded]);

  const handleSendMessage = useCallback(async () => {
    if (!message.trim() || isLoading || isInputDisabled || isSwitchingMode) return;

    const userMessage = { type: 'user' as const, message: message.trim() };
    onChatUpdate(userMessage);
    setMessage('');
    
    // Reset textarea height
    if (textareaRef.current) {
      textareaRef.current.style.height = '48px';
    }
    
    setIsLoading(true);

    try {
      const response = await (customChatHandler ? customChatHandler(userMessage.message, resolvedMode) : aiApi.chatWithMaterials({
        session_id: sessionId,
        message: userMessage.message,
        mode: resolvedMode
      }));
      const aiMessage = { type: 'ai' as const, message: response.response };
      onChatUpdate(aiMessage);
    } catch (error) {
      console.error('Chat error:', error);
      const errorMessage = { 
        type: 'ai' as const, 
        message: t('sessions:flow.chat.chatError', 'Sorry, I encountered an error. Please try again.') 
      };
      onChatUpdate(errorMessage);
    } finally {
      setIsLoading(false);
    }
  }, [message, isLoading, isInputDisabled, isSwitchingMode, sessionId, t, onChatUpdate, customChatHandler, resolvedMode]);

  // Handle pending message
  useEffect(() => {
    if (pendingMessage && isOpen && !isMinimized && !isLoading) {
      // Set the message and send it
      setMessage(pendingMessage);
      
      // Send the message after a brief delay to ensure state is updated
      setTimeout(() => {
        handleSendMessage();
        // Clear the pending message
        if (onPendingMessageHandled) {
          onPendingMessageHandled();
        }
      }, 100);
    }
  }, [pendingMessage, isOpen, isMinimized, isLoading, handleSendMessage, onPendingMessageHandled]);

  const handleKeyPress = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  }, [handleSendMessage]);

  // Debounced textarea resize to improve performance
  const handleTextareaInput = useCallback((e: React.FormEvent<HTMLTextAreaElement>) => {
    const target = e.target as HTMLTextAreaElement;
    // Reset height to recalculate
    target.style.height = 'auto';
    // Set new height based on content
    const newHeight = Math.min(target.scrollHeight, 120);
    target.style.height = `${newHeight}px`;
  }, []);

  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    setIsDragging(true);
    setDragStart({
      x: e.clientX - position.x,
      y: e.clientY - position.y
    });
  }, [position]);

  const handleMouseMove = useCallback((e: MouseEvent) => {
    if (isDragging) {
      const currentWidth = isMinimized ? 120 : size.width;
      const currentHeight = isMinimized ? 60 : size.height;
      setPosition({
        x: Math.max(0, Math.min(window.innerWidth - currentWidth, e.clientX - dragStart.x)),
        y: Math.max(80, Math.min(window.innerHeight - currentHeight, e.clientY - dragStart.y)) // Don't allow dragging above navbar (80px)
      });
    }
  }, [isDragging, dragStart, size, isMinimized]);

  // Resize handlers - only for desktop
  const handleResizeStart = useCallback((e: React.MouseEvent) => {
    if (isMobile) return; // Disable resize on mobile
    e.stopPropagation();
    setIsResizing(true);
    setResizeStart({
      x: e.clientX,
      y: e.clientY,
      width: size.width,
      height: size.height
    });
    // Store initial position for top-left resize
    setDragStart({
      x: position.x,
      y: position.y
    });
  }, [size, position, isMobile]);

  const handleResizeMove = useCallback((e: MouseEvent) => {
    if (!isResizing || isMobile) return; // Disable resize on mobile
    
    const deltaX = e.clientX - resizeStart.x;
    const deltaY = e.clientY - resizeStart.y;
    
    // Desired new size before clamping
    let newWidth = resizeStart.width - deltaX;
    let newHeight = resizeStart.height - deltaY;

    // Enforce minimum size constraints
    newWidth = Math.max(200, newWidth);
    newHeight = Math.max(300, newHeight);

    // Enforce maximum size so the widget never extends outside the viewport
    newWidth = Math.min(newWidth, window.innerWidth);
    // Reserve 80px for the navbar/top-offset when calculating max height
    newHeight = Math.min(newHeight, window.innerHeight - 80);

    // Calculate the new top-left corner position (widget is resized from top-left)
    const widthDiff = newWidth - resizeStart.width;
    const heightDiff = newHeight - resizeStart.height;

    let newX = dragStart.x - widthDiff;
    let newY = dragStart.y - heightDiff;

    // Clamp position so the top-left corner never leaves the viewport bounds
    newX = Math.max(0, Math.min(window.innerWidth - newWidth, newX));
    newY = Math.max(80, Math.min(window.innerHeight - newHeight, newY));

    // Final safeguard: if after clamping the bottom/right edge is still outside the viewport, adjust size accordingly
    if (newY + newHeight > window.innerHeight) {
      newHeight = Math.max(300, window.innerHeight - newY);
    }
    if (newX + newWidth > window.innerWidth) {
      newWidth = Math.max(200, window.innerWidth - newX);
    }

    setSize({ width: newWidth, height: newHeight });
    setPosition({ x: newX, y: newY });
  }, [isResizing, resizeStart, dragStart, isMobile]);

  const handleResizeEnd = useCallback(() => {
    setIsResizing(false);
  }, []);

  const handleMouseUp = useCallback(() => {
    setIsDragging(false);
  }, []);

  // Handle window resize for mobile responsiveness
  useEffect(() => {
    const handleResize = () => {
      if (typeof window === 'undefined') return;
      
      const newIsMobile = window.innerWidth < 1024;
      
      if (newIsMobile) {
        // Update size and position for mobile
        setSize({
          width: window.innerWidth,
          height: window.innerHeight - 64
        });
        setPosition({ x: 0, y: 64 });
      } else if (!newIsMobile && window.innerWidth >= 1024) {
        // Reset to desktop size and position if switching from mobile to desktop
        setSize({
          width: 384,
          height: window.innerHeight - 120
        });
        setPosition({
          x: Math.max(16, window.innerWidth - 384 - 16),
          y: 80
        });
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, [isDragging, handleMouseMove, handleMouseUp]);

  useEffect(() => {
    if (isResizing) {
      document.addEventListener('mousemove', handleResizeMove);
      document.addEventListener('mouseup', handleResizeEnd);
      return () => {
        document.removeEventListener('mousemove', handleResizeMove);
        document.removeEventListener('mouseup', handleResizeEnd);
      };
    }
  }, [isResizing, handleResizeMove, handleResizeEnd]);

  // Memoize styles to prevent recalculation
  const containerStyle = useMemo(() => {
    if (embedded) {
      return {
        left: '0px',
        top: '0px',
        width: '100%',
        height: '100%',
        cursor: 'default'
      };
    }

    // Mobile full screen styles
    if (isMobile) {
      return {
        left: '0px',
        top: '64px', // Below navbar
        width: '100vw',
        height: isMinimized ? '60px' : 'calc(100vh - 64px)',
        cursor: isDragging ? 'grabbing' : 'default'
      };
    }
    
    // Desktop styles
    return {
      left: `${position.x}px`,
      top: `${position.y}px`,
      width: isMinimized ? '120px' : `${size.width}px`,
      height: isMinimized ? '60px' : `${size.height}px`,
      cursor: isDragging ? 'grabbing' : isResizing ? 'se-resize' : 'default'
    };
  }, [position, size, isMinimized, isDragging, isResizing, isMobile, isFullscreen, embedded]);

  if (!isOpen) return null;

  const chatWidget = (
    <>
      {/* Custom scrollbar and typing animation styles */}
      <style>{`
        .custom-scrollbar::-webkit-scrollbar {
          width: 4px;
        }
        .custom-scrollbar::-webkit-scrollbar-track {
          background: transparent;
          margin: 2px;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb {
          background: rgba(247, 103, 0, 0.3);
          border-radius: 2px;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb:hover {
          background: rgba(247, 103, 0, 0.5);
        }
        .custom-scrollbar::-webkit-scrollbar-corner {
          background: transparent;
        }
        
        /* Minimal scrollbar for textarea */
        .textarea-scrollbar::-webkit-scrollbar {
          width: 3px;
        }
        .textarea-scrollbar::-webkit-scrollbar-track {
          background: transparent;
        }
        .textarea-scrollbar::-webkit-scrollbar-thumb {
          background: rgba(247, 103, 0, 0.2);
          border-radius: 2px;
        }
        .textarea-scrollbar::-webkit-scrollbar-thumb:hover {
          background: rgba(247, 103, 0, 0.4);
        }
        
        /* Ensure KaTeX elements stay within chat widget stacking context */
        .katex, .katex-display, .katex * {
          z-index: auto !important;
          position: relative !important;
        }
        
        @keyframes typing-dots {
          0%, 60%, 100% {
            opacity: 0.2;
            transform: scale(0.6);
          }
          30% {
            opacity: 1;
            transform: scale(1.2);
          }
        }
        
        .typing-dot-1 {
          animation: typing-dots 1.4s infinite;
          animation-delay: 0s;
        }
        
        .typing-dot-2 {
          animation: typing-dots 1.4s infinite;
          animation-delay: 0.2s;
        }
        
        .typing-dot-3 {
          animation: typing-dots 1.4s infinite;
          animation-delay: 0.4s;
        }
        
        .dragging-no-select * {
          user-select: none !important;
          -webkit-user-select: none !important;
          -moz-user-select: none !important;
          -ms-user-select: none !important;
        }
      `}</style>
      
      <div 
        className={`${embedded ? 'absolute inset-0' : 'fixed'} ${isFullscreen ? 'z-[9999]' : embedded ? 'z-[1]' : 'z-[70]'} select-none ${isDragging ? 'dragging-no-select' : ''}`}
        style={containerStyle}
        dir={effectiveRTL ? 'rtl' : 'ltr'}
      >
        <div 
          className="relative h-full flex flex-col bg-white rounded-lg shadow-2xl border border-secondary-200"
          style={{ borderRadius: (isMobile || isFullscreen) ? '0' : '8px' }} // Remove border radius on mobile and fullscreen
        >
        {isOverlayVisible && overlayContent && (
          <div className="absolute inset-0 z-40 flex h-full w-full pointer-events-none">
            {overlayContent}
          </div>
        )}
        {/* Resize Button - Top Left (only when allowed, not minimized, not mobile, and not fullscreen) */}
        {allowResize && !isMinimized && !isMobile && !isFullscreen && (
          <button
            onMouseDown={handleResizeStart}
            className="absolute top-2 left-2 z-20 w-6 h-6 bg-vibrant-orange hover:bg-vibrant-orange/90 text-white rounded-full flex items-center justify-center transition-colors shadow-lg cursor-nw-resize"
          >
            <Move className="w-3 h-3" />
          </button>
        )}
        
        {/* Fullscreen Button - Top Left (only when not minimized, not mobile, and not in fullscreen) */}
        {!isMinimized && !isMobile && !isFullscreen && (
          <button
            onClick={() => {
              // Save current scroll position
              if (messagesContainerRef.current) {
                setSavedScrollPosition(messagesContainerRef.current.scrollTop);
              }

              // Save current size and position
              setPreFullscreenSize(size);
              setPreFullscreenPosition(position);

              // Set to fullscreen size and position
              setSize({ width: window.innerWidth, height: window.innerHeight });
              setPosition({ x: 0, y: 0 });
              setIsFullscreen(true);
              setTimeout(scrollToBottom, 100);
            }}
            className="absolute top-2 left-10 z-20 w-6 h-6 bg-vibrant-orange hover:bg-vibrant-orange/90 text-white rounded-full flex items-center justify-center transition-colors shadow-lg"
            onMouseDown={(e) => e.stopPropagation()}
            title={effectiveRTL ? 'מסך מלא' : 'Fullscreen'}
          >
            <Expand className="w-3 h-3" />
          </button>
        )}
        
        {/* Fullscreen Header (only when in fullscreen) */}
        {isFullscreen && (
          <div className="absolute top-0 left-0 right-0 h-10 z-[15] bg-white hover:bg-gradient-to-r hover:from-vibrant-orange/10 hover:via-vibrant-orange/5 hover:to-vibrant-orange/10 transition-colors border-b border-gray-200 flex items-center justify-between px-4">
            <div className="flex-1" /> {/* Spacer */}
            <button
              onClick={() => {
                // Restore previous size and position
                if (preFullscreenSize && preFullscreenPosition) {
                  setSize(preFullscreenSize);
                  setPosition(preFullscreenPosition);
                }
                setIsFullscreen(false);
                setPreFullscreenSize(null);
                setPreFullscreenPosition(null);

                // Restore scroll position after DOM updates
                if (savedScrollPosition !== null && messagesContainerRef.current) {
                  setTimeout(() => {
                    if (messagesContainerRef.current) {
                      messagesContainerRef.current.scrollTop = savedScrollPosition;
                    }
                    setSavedScrollPosition(null);
                  }, 100);
                }
              }}
              className="w-6 h-6 bg-vibrant-orange hover:bg-vibrant-orange/90 text-white rounded-full flex items-center justify-center transition-colors shadow-lg"
              onMouseDown={(e) => e.stopPropagation()}
              title={effectiveRTL ? 'יציאה ממסך מלא' : 'Exit Fullscreen'}
            >
              <Minimize2 className="w-3 h-3" />
            </button>
          </div>
        )}

        {/* Header area for dragging (only when not minimized, not mobile, and not fullscreen) */}
        {!embedded && !isMinimized && !isMobile && !isFullscreen && (
          <div
            className="absolute top-0 left-0 right-0 h-10 z-[15] bg-white hover:bg-gradient-to-r hover:from-vibrant-orange/10 hover:via-vibrant-orange/5 hover:to-vibrant-orange/10 rounded-t-lg transition-colors cursor-grab hover:cursor-grab active:cursor-grabbing border-b border-gray-200"
            onMouseDown={handleMouseDown}
            title={effectiveRTL ? 'גרור להזיז' : 'Drag to move'}
          />
        )}

                 {/* Minimized state - compact layout */}
        {isMinimized ? (
          <div 
            className="h-full flex items-center justify-center gap-2 px-2 cursor-grab hover:cursor-grab active:cursor-grabbing" 
            style={{ direction: 'ltr' }}
            onMouseDown={!isMobile ? handleMouseDown : undefined}
          >
            <div className="w-8 h-8 bg-gradient-to-br from-vibrant-orange to-vibrant-orange/90 rounded-full flex items-center justify-center">
              <img
                src={resolvedAssistantAvatarSrc}
                alt={resolvedAssistantName}
                className="w-20 h-20 object-contain"
              />
            </div>
            <button
              onClick={handleUnminimize}
              className="w-6 h-6 bg-vibrant-orange hover:bg-vibrant-orange/90 text-white rounded-full flex items-center justify-center transition-colors shadow-lg"
              onMouseDown={(e) => e.stopPropagation()}
            >
              <Maximize2 className="w-3 h-3" />
            </button>
            {showCloseButton && (
              <button
                onClick={onClose}
                className="w-6 h-6 bg-vibrant-orange hover:bg-vibrant-orange/90 text-white rounded-full flex items-center justify-center transition-colors shadow-lg"
                onMouseDown={(e) => e.stopPropagation()}
              >
                <X className="w-3 h-3" />
              </button>
            )}
          </div>
        ) : (
          <>
            {/* Controls - Top Right (only when not minimized and not fullscreen) */}
            {!isFullscreen && (
              <div className={`absolute top-2 right-2 z-20 flex items-center gap-1 ${effectiveRTL ? 'flex-row-reverse' : ''}`}>
                {showMinimizeButton && !isMobile && (
                  <button
                    onClick={() => {
                      if (effectiveRTL && !isMobile) {
                        // For RTL, position minimized box at top-right of original position
                        setPosition(prev => ({ 
                          ...prev, 
                          x: Math.min(window.innerWidth - 120, prev.x + (size.width - 120))
                        }));
                      }
                      setIsMinimized(true);
                    }}
                    className="w-6 h-6 bg-vibrant-orange hover:bg-vibrant-orange/90 text-white rounded-full flex items-center justify-center transition-colors shadow-lg"
                    onMouseDown={(e) => e.stopPropagation()}
                  >
                    <Minimize2 className="w-3 h-3" />
                  </button>
                )}
                {showCloseButton && (
                  <button
                    onClick={onClose}
                    className="w-6 h-6 bg-vibrant-orange hover:bg-vibrant-orange/90 text-white rounded-full flex items-center justify-center transition-colors shadow-lg"
                    onMouseDown={(e) => e.stopPropagation()}
                  >
                    <X className="w-3 h-3" />
                  </button>
                )}
              </div>
            )}
          </>
        )}

        {!isMinimized && (
          <>
            {/* Messages */}
            <div 
              ref={messagesContainerRef}
              className="flex-1 overflow-y-auto pt-12 pb-2 px-2 space-y-2 custom-scrollbar min-h-0 select-text relative z-[5]"
              style={{
                scrollbarWidth: 'thin',
                scrollbarColor: 'rgba(247, 103, 0, 0.3) transparent',
                direction: 'ltr' // Force scrollbar to always appear on the right
              }}
            >
              {chatHistory.length === 0 && (
                <div className="flex items-center justify-center h-full min-h-[200px]" style={{ direction: effectiveRTL ? 'rtl' : 'ltr' }}>
                  <div className="text-center">
                    <div className="w-20 h-20 bg-gradient-to-br from-vibrant-orange to-vibrant-orange/90 rounded-full flex items-center justify-center mx-auto mb-3">
                      <img
                        src={resolvedAssistantAvatarSrc}
                        alt={resolvedAssistantName}
                        className="w-20 h-20 object-contain"
                      />
                    </div>
                    <p className={`text-surface-muted text-sm ${effectiveRTL ? 'text-right' : ''}`}>
                      {resolvedWelcomeMessage}
                    </p>
                  </div>
                </div>
              )}
              
              {chatHistory.map((msg, index) => (
                <ChatMessage 
                  key={index}
                  msg={msg}
                  index={index}
                  effectiveRTL={effectiveRTL}
                  messageFeedback={messageFeedback}
                  onMessageFeedback={onMessageFeedback}
                  assistantName={resolvedAssistantName}
                  assistantAvatarSrc={resolvedAssistantAvatarSrc}
                />
              ))}
              
              {isLoading && (
                <div className={`flex ${effectiveRTL ? 'justify-end' : 'justify-start'}`} style={{ direction: effectiveRTL ? 'rtl' : 'ltr' }}>
                  <div className="bg-surface-muted/10 p-2 rounded-2xl border border-surface-muted/30">
                    <div className={`flex items-center ${effectiveRTL ? 'space-x-reverse space-x-2' : 'space-x-2'}`}>
                      <div className="w-5 h-5 bg-gradient-to-br from-vibrant-orange to-vibrant-orange/90 rounded-full flex items-center justify-center flex-shrink-0">
                        <img
                          src={resolvedAssistantAvatarSrc}
                          alt={resolvedAssistantName}
                          className="w-20 h-20 object-contain animate-pulse"
                        />
                      </div>
                      <div className={`flex ${effectiveRTL ? 'space-x-reverse space-x-1' : 'space-x-1'}`}>
                        <div className="w-2 h-2 bg-vibrant-orange rounded-full typing-dot-1"></div>
                        <div className="w-2 h-2 bg-vibrant-orange rounded-full typing-dot-2"></div>
                        <div className="w-2 h-2 bg-vibrant-orange rounded-full typing-dot-3"></div>
                      </div>
                    </div>
                  </div>
                </div>
              )}
              
              <div ref={messagesEndRef} />
            </div>

            {/* Input */}
            <div className="p-4 bg-surface-muted/5">
              <div className="rounded-2xl border border-surface-muted/10 bg-white p-4 shadow-lg">
                <div className={`flex flex-col gap-4 ${effectiveRTL ? 'items-end' : 'items-start'}`}>
                  <div className={`flex w-full items-start gap-4 ${effectiveRTL ? 'flex-row-reverse' : ''}`}>
                    <button
                      type="button"
                      onClick={handleSendMessage}
                      disabled={isSendDisabled}
                      className={`flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full bg-vibrant-orange text-white transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-vibrant-orange/40 ${isSendDisabled ? 'cursor-not-allowed opacity-50' : 'hover:bg-[#ff7a1a]'}`}
                      aria-label={t('common:actions.submit', effectiveRTL ? 'שלח' : 'Send')}
                      title={t('common:actions.submit', effectiveRTL ? 'שלח' : 'Send')}
                    >
                      <ArrowIcon className="h-4 w-4" strokeWidth={2.5} />
                    </button>
                    <div className={`flex-1 rounded-2xl border border-surface-muted/40 bg-surface-muted/10 px-5 py-3 transition focus-within:border-vibrant-orange/60 focus-within:bg-white focus-within:ring-2 focus-within:ring-vibrant-orange/30 ${isInputDisabled ? 'opacity-60' : ''}`}>
                      <textarea
                        ref={textareaRef}
                        value={message}
                        onChange={(e) => setMessage(e.target.value)}
                        onKeyPress={handleKeyPress}
                        placeholder={resolvedInputPlaceholder}
                        className={`w-full resize-none border-none bg-transparent text-base leading-relaxed text-surface-primary placeholder-surface-muted focus:outline-none focus:ring-0 textarea-scrollbar ${effectiveRTL ? 'text-right' : 'text-left'}`}
                        rows={1}
                        style={{
                          minHeight: '64px',
                          maxHeight: '160px',
                          lineHeight: '1.5',
                          scrollbarWidth: 'thin',
                          scrollbarColor: 'rgba(247, 103, 0, 0.2) transparent'
                        }}
                        dir={effectiveRTL ? 'rtl' : 'ltr'}
                        onInput={handleTextareaInput}
                        disabled={isInputDisabled}
                      />
                    </div>
                  </div>

                  {enableModeSwitch && (
                    <div className={`flex w-full flex-wrap items-center gap-2 ${effectiveRTL ? 'justify-end text-right' : 'justify-start text-left'}`}>
                      <div ref={modeDropdownRef} className="relative">
                        <button
                          type="button"
                          onClick={() => {
                            if (modePickerDisabled) return;
                            setIsModeDropdownOpen((prev) => !prev);
                          }}
                          disabled={modePickerDisabled}
                          aria-haspopup="listbox"
                          aria-expanded={isModeDropdownOpen}
                          aria-label={modePickerLabel}
                          className={`inline-flex items-center gap-2 rounded-full border border-surface-muted/20 bg-white px-4 py-2 text-sm font-medium text-surface-primary shadow-sm transition focus:outline-none focus:ring-2 focus:ring-vibrant-orange/30 ${modePickerDisabled ? 'cursor-not-allowed opacity-60' : 'hover:border-vibrant-orange/30 hover:text-vibrant-orange'}`}
                        >
                          <span>{modeLabels[resolvedMode] ?? resolvedMode}</span>
                          <ChevronDown className={`h-4 w-4 transition-transform ${isModeDropdownOpen ? 'rotate-180 text-vibrant-orange' : 'text-surface-muted'}`} />
                        </button>
                        {isModeDropdownOpen && (
                          <div className={`absolute ${effectiveRTL ? 'right-0' : 'left-0'} bottom-full z-30 mb-2 w-48 rounded-2xl border border-surface-muted/20 bg-white p-1 shadow-xl`}>
                            {availableModeOptions.map((modeOption) => {
                              const isActive = modeOption === resolvedMode;
                              return (
                                <button
                                  key={modeOption}
                                  type="button"
                                  onClick={() => handleModeSelect(modeOption)}
                                  className={`flex w-full items-center justify-between gap-3 rounded-xl px-3 py-2 text-sm transition ${
                                    isActive
                                      ? 'bg-vibrant-orange/10 text-vibrant-orange'
                                      : 'text-surface-primary hover:bg-surface-muted/10'
                                  }`}
                                  role="option"
                                  aria-selected={isActive}
                                >
                                  <span className="truncate">{modeLabels[modeOption] ?? modeOption}</span>
                                  {isActive && <Check className="h-4 w-4 text-vibrant-orange" />}
                                </button>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {modeSwitchError && enableModeSwitch && (
                    <p className={`text-xs text-red-500 ${effectiveRTL ? 'text-right' : 'text-left'}`}>
                      {modeSwitchError}
                    </p>
                  )}
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
    </>
  );

  // Use React Portal for fullscreen mode to escape stacking context
  if (isFullscreen) {
    return createPortal(chatWidget, document.body);
  }

  return chatWidget;
};

export default ChatWidget; 
